import React, {useState} from 'react';
import {Accordion, Alert, Button, Modal, Table, Spinner} from "react-bootstrap";
import PropTypes from "prop-types";
import moment from "moment/moment";
import {useProjektData} from "../../../../../api/domain/elementyRejstru/queries/useProjektData.jsx";
import {usePlanHarmData} from "../../../../../api/domain/elementyRejstru/queries/usePlanHarmData.jsx";
import {toCurrency} from "../../../../utils/helpers/index.js";
import {Select} from "../../../../common/inputs/index.js";
import DateTime from "../../../../common/inputs/DateTime.jsx";
import {optionsSprawozdanieStatus} from "../../../../utils/options.js";
import {SimpleTable} from "../../../../common/simpleTable/SimpleTable.jsx";
import {useDodajZdarzenieProcesuSprawozdanie} from "../../../../../api/domain/sprawozdania/mutations/useDodajZdarzenieProcesuSprawozdanie.jsx";
import {useDostepneStatusy} from "../../../../../api/domain/sprawozdania/queries/useDostepneStatusy.jsx";
import {useHistoriaSprawozdanie} from "../../../../../api/domain/sprawozdania/queries/useHistoriaSprawozdanie.jsx";
import {tableHistoriaProjektu} from "../../../rejestr/lib/columns/tableHistoriaProjektu.jsx";



export const ModalInfoProjekt = ({ show, handleClose, rejestrId }) => {

    // Pobieranie danych KP i PH
    const {
        data: projektData,
        isLoading: isLoadingKP,
        isError: isErrorKP
    } = useProjektData(rejestrId, show);

    const isCategoryB = projektData?.kategoria === 'B';

    const {
        data: planHarmData,
        isLoading: isLoadingPH,
        isError: isErrorPH
    } = usePlanHarmData(rejestrId, null, show && !isCategoryB && !isLoadingKP);

    const dataKP = projektData;
    const dataPH = isCategoryB ? null : planHarmData;

    const ryzykaList = Array.isArray(dataKP?.ryzyka_list) ? dataKP.ryzyka_list : [];

    const PHASE_LABELS = {
        inicjacja: 'Etap Inicjacji',
        przygotowania: 'Etap Przygotowania',
        realizacja: 'Etap Realizacji',
        zamykania: 'Etap Zamykania',
    };

    const isLoading = isLoadingKP || (!isCategoryB && isLoadingPH);
    const isError = isErrorKP || (!isCategoryB && isErrorPH);

    const calculateTotalBudget = (phData) => {
        if (!phData) return 0;
        const total = (
            parseFloat(phData.inicjacja_budzet_planowany || 0) +
            parseFloat(phData.przygotowania_budzet_planowany || 0) +
            parseFloat(phData.realizacja_budzet_planowany || 0) +
            parseFloat(phData.zamykania_budzet_planowany || 0)
        );
        return total;
    };

    const renderTable = (dataKP, dataPH) => (
        <div className="d-flex flex-column gap-3">
            <Table striped bordered hover size="sm" className="mb-0">
                <tbody>
                <tr className="table-primary">
                    <td colSpan="2" className="fw-bold text-center fs-5">Karta Projektu</td>
                </tr>
                <tr>
                    <th style={{ width: '30%' }}>ID Rejestru</th>
                    <td className="text-end">{rejestrId}</td>
                </tr>
                <tr>
                    <th>Numer projektu</th>
                    <td className="text-end">{dataKP.numer || '-'}</td>
                </tr>
                <tr>
                    <th>Tytuł projektu</th>
                    <td className="text-end">{dataKP.tytul || '-'}</td>
                </tr>
                <tr>
                    <th>Kategoria</th>
                    <td className="text-end"><span className="badge bg-info text-dark">{dataKP.kategoria || '-'}</span></td>
                </tr>
                <tr>
                    <th>Data rozpoczęcia</th>
                    <td className="text-end">{dataKP.data_rozpoczecia || '-'}</td>
                </tr>
                <tr>
                    <th>Data zakończenia</th>
                    <td className="text-end">{dataKP.data_zakonczenia || '-'}</td>
                </tr>
                <tr>
                    <th>Szacowana wartość</th>
                    <td className="text-end">{dataKP.wartosc_calkowita ? `${toCurrency(dataKP.wartosc_calkowita)}` : '-'}</td>
                </tr>
                <tr>
                    <th>Kierownik Projektu</th>
                    <td className="text-end">
                        {dataKP.kierownik_imie || '-'} {dataKP.kierownik_nazwisko || '-'}
                        {(dataKP.kierownik_email || dataKP.kierownik_telefon) && <br/>}
                        <small className="text-muted">
                            {dataKP.kierownik_email && dataKP.kierownik_email + ' | '}
                            {dataKP.kierownik_telefon || ''}
                        </small>
                    </td>
                </tr>
                </tbody>
            </Table>

            <Accordion alwaysOpen={false} defaultActiveKey={[]} className="rounded-0">
                <Accordion.Item eventKey="cel_zakres" className="rounded-0">
                    <Accordion.Header className="rounded-0">
                        <span className="fw-bold">Cel i zakres projektu</span>
                    </Accordion.Header>
                    <Accordion.Body className="p-0 rounded-0">
                        <Table striped bordered hover size="sm" className="mb-0">
                            <tbody>
                            {dataKP.cel_glowny && (
                                <tr>
                                    <th style={{ width: '30%' }}>Cel główny</th>
                                    <td>{dataKP.cel_glowny}</td>
                                </tr>
                            )}
                            {dataKP.cel_projektu && (
                                <tr>
                                    <th>Cel projektu</th>
                                    <td>{dataKP.cel_projektu}</td>
                                </tr>
                            )}
                            {dataKP.wskazniki_realizacji && (
                                <tr>
                                    <th>Zakres projektu</th>
                                    <td>{dataKP.wskazniki_realizacji}</td>
                                </tr>
                            )}
                            {dataKP.uzasadnienie_celowosci && (
                                <tr>
                                    <th>Uzasadnienie celowości realizacji projektu</th>
                                    <td>{dataKP.uzasadnienie_celowosci}</td>
                                </tr>
                            )}
                            {dataKP.grupa_docelowa && (
                                <tr>
                                    <th>Grupa docelowa projektu / odbiorcy ostateczni wsparcia (jeśli dotyczy)</th>
                                    <td>{dataKP.grupa_docelowa}</td>
                                </tr>
                            )}
                            {!dataKP.cel_glowny && !dataKP.cel_projektu && !dataKP.wskazniki_realizacji && !dataKP.uzasadnienie_celowosci && !dataKP.grupa_docelowa && (
                                <tr>
                                    <td colSpan="2" className="text-muted text-center">Brak danych.</td>
                                </tr>
                            )}
                            </tbody>
                        </Table>
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="ryzyka" className="rounded-0">
                    <Accordion.Header className="rounded-0">
                        <span className="fw-bold">Ryzyka projektu ({ryzykaList.length})</span>
                    </Accordion.Header>
                    <Accordion.Body className="p-2 rounded-0">
                        {ryzykaList.length > 0 ? (
                            <Accordion alwaysOpen={false} defaultActiveKey={[]} className="rounded-0">
                                {ryzykaList.map((r, idx) => {
                                    const headerText = r?.ryzyko || r?.nazwa ? `Ryzyko ${idx + 1} - ${r?.ryzyko || r?.nazwa}` : `Ryzyko ${idx + 1}`;

                                    return (
                                        <Accordion.Item eventKey={String(idx)} key={idx} className="rounded-0">
                                            <Accordion.Header className="rounded-0">{headerText}</Accordion.Header>
                                            <Accordion.Body className="p-0 rounded-0">
                                                <Table striped bordered hover size="sm" className="mb-0">
                                                    <tbody>
                                                    <tr>
                                                        <th style={{ width: '30%' }}>Kategoria</th>
                                                        <td className="text-end">{r?.kategoria || '-'}</td>
                                                    </tr>
                                                    {!isCategoryB && (
                                                    <>
                                                    <tr>
                                                        <th>Prawdopodobieństwo</th>
                                                        <td className="text-end">{r?.prawdopodobienstwo || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Skutek</th>
                                                        <td className="text-end">{r?.skutek || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Poziom</th>
                                                        <td className="text-end">{r?.poziom || '-'}</td>
                                                    </tr>
                                                    </>
                                                    )}
                                                    </tbody>
                                                </Table>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    );
                                })}
                            </Accordion>
                        ) : (
                            <Alert variant="secondary" className="mb-0 rounded-0">
                                Brak ryzyk do wyświetlenia.
                            </Alert>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            {!isCategoryB && (
                <Table striped bordered hover size="sm" className="mb-0">
                    <tbody>
                    <tr className="table-primary">
                        <td colSpan="2" className="fw-bold text-center fs-5">Planowany Harmonogram</td>
                    </tr>
                    <tr>
                        <th>Status Harmonogramu</th>
                        <td className="text-end">{dataKP.plan_harm_status || 'Brak'}</td>
                    </tr>
                    <tr>
                        <th>ID Harmonogramu</th>
                        <td className="text-end">{dataKP.plan_harm_id || dataPH?.id || '-'}</td>
                    </tr>
                    <tr>
                        <th>Łączny Budżet</th>
                        <td className="text-end">{dataPH ? toCurrency(calculateTotalBudget(dataPH)) : '-'}</td>
                    </tr>
                    </tbody>
                </Table>
            )}

            {!isCategoryB && dataPH ? (
                <Accordion alwaysOpen={false} defaultActiveKey={[]} className="rounded-0">
                    {['inicjacja', 'przygotowania', 'realizacja', 'zamykania'].map((faza) => (
                        <Accordion.Item eventKey={faza} key={faza} className="rounded-0">
                            <Accordion.Header className="rounded-0">{PHASE_LABELS[faza] || faza}</Accordion.Header>
                            <Accordion.Body className="p-0 rounded-0">
                                <Table striped bordered hover size="sm" className="mb-0">
                                    <tbody>
                                    <tr>
                                        <th style={{ width: '30%' }}>Data Rozpoczęcia</th>
                                        <td className="text-end">{dataPH[`${faza}_data_rozpoczecia`] || '-'}</td>
                                    </tr>
                                    <tr>
                                        <th>Data Zakończenia</th>
                                        <td className="text-end">{dataPH[`${faza}_data_zakonczenia`] || '-'}</td>
                                    </tr>
                                    <tr>
                                        <th>Budżet Planowany</th>
                                        <td className="text-end">{toCurrency(dataPH[`${faza}_budzet_planowany`])}</td>
                                    </tr>
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>
            ) : !isCategoryB ? (
                <Alert variant="secondary" className="mb-0 rounded-0">
                    Brak szczegółowych danych Planu Harmonogramu.
                </Alert>
            ) : null}
        </div>
    );

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Informacje o projekcie ({rejestrId})</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <div className="d-flex justify-content-center p-4">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : isError ? (
                    <Alert variant="danger">Nie udało się pobrać danych projektu lub harmonogramu.</Alert>
                ) : dataKP && typeof dataKP === 'object' && Object.keys(dataKP).length > 0 ? (
                    renderTable(dataKP, dataPH)
                ) : (
                    <Alert variant="warning">
                        Brak danych Karty Projektu dla ID: {rejestrId}.
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Zamknij
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalInfoProjekt.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    rejestrId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const ModalHistoriaSprawozdanie = ({currPozycja, show, handleClose}) => {
    const {data: historiaSprawozdanie} = useHistoriaSprawozdanie(currPozycja?.id, show)
    const detailKey = 'historia_sprawozdanie'
    const filteredHistoria = Array.isArray(currPozycja?.sprawozdanieIds)
        ? (historiaSprawozdanie?.list || []).filter((item) => currPozycja.sprawozdanieIds.includes(item?.sprawozdanie_id))
        : historiaSprawozdanie?.list
    const modalTitle = currPozycja?.historiaLabel
        ? `Historia sprawozdania ${currPozycja.historiaLabel} dla projektu o id ${currPozycja.id}`
        : `Historia sprawozdania dla projektu o id ${currPozycja?.id}`

    return (
        <Modal size={'xl'} show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <SimpleTable
                    detailKey={detailKey}
                    columns={tableHistoriaProjektu}
                    addTh={() => null}
                    addTd={() => null}
                    data={filteredHistoria}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant='secondary' onClick={handleClose}>
                    Zamknij
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

ModalHistoriaSprawozdanie.propTypes = {
    currPozycja: PropTypes.object,
    show: PropTypes.bool,
    handleClose: PropTypes.func,
}

export const ModalProcesSprawozdania = ({currPozycja, show, handleClose}) => {
    const dodajZdarzenie = useDodajZdarzenieProcesuSprawozdanie()
    const sprawozdanieId = currPozycja?.sprawozdanie_id || currPozycja?.id
    const {data: dostepneStatusy} = useDostepneStatusy(sprawozdanieId, show && !!sprawozdanieId)

    const [form, setForm] = useState(
        {
            status: currPozycja?.status,
            data_zdarzenia: currPozycja?.data_zdarzenia,
            uwagi: currPozycja?.uwagi,
            wersja: currPozycja?.wersja,
            rejestr_id: currPozycja?.rejestrId,
            sprawozdanie_id: currPozycja?.sprawozdanie_id || currPozycja?.id,
            id: currPozycja?.id
        }
    )

    const updateForm = (field, value) => setForm({...form, [field]: value})

    const filteredStatusOptions = optionsSprawozdanieStatus.filter(
        option => dostepneStatusy?.dostepne_statusy?.includes(option.id) || option.id === ''
    )
    const hasAvailableStatuses = filteredStatusOptions.length > 0

    return (
        <Modal size={'lg'} show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Proces sprawozdania</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    Dodawanie / edycja zdarzenia procesu sprawozdania o id {currPozycja?.id}
                </p>
                <p className="text-muted small">
                    Aktualny status: <strong>{dostepneStatusy?.aktualny_status || '-'}</strong>
                </p>
                {!hasAvailableStatuses && (
                    <div className="alert alert-info">
                        {['ZAKOŃCZONY', 'ZAKOŃCZONA'].includes(dostepneStatusy?.aktualny_status)
                            ? 'Proces został zakończony. Brak dostępnych dalszych kroków.'
                            : 'Następny krok w procesie należy do właściciela projektu.'}
                    </div>
                )}
                {hasAvailableStatuses && (
                    <>
                        <Select onChange={(e) => updateForm('status', e.target.value)} value={form.status} title={'Status'}
                                label={'Status'}
                                options={filteredStatusOptions}
                        />

                        <DateTime
                            hideTime
                            title={'Data zdarzenia'}
                            label={'Data zdarzenia'}
                            value={form?.data_zdarzenia}
                            onChange={(e) =>
                                updateForm('data_zdarzenia', !e ? null : moment(e).format('YYYY-MM-DD'))
                            }
                            dateFormat={'YYYY-MM-DD'}
                            timeFormat={false}
                        /></>
                )}

            </Modal.Body>
            <Modal.Footer>
                <Button variant='secondary' onClick={handleClose}>
                    Zamknij
                </Button>
                <Button
                    variant='success'
                    disabled={!form.status || !hasAvailableStatuses}
                    onClick={async () => {
                        const finalSprawozdanieId = form.sprawozdanie_id || currPozycja?.sprawozdanie_id;
                        const finalWersja = form.wersja || currPozycja?.wersja;

                        await dodajZdarzenie.mutate({
                            ...form,
                            rejestr_id: currPozycja?.rejestrId,
                            sprawozdanie_id: finalSprawozdanieId,
                            wersja: finalWersja,
                            id: form.id || currPozycja?.id
                        })
                        setForm({})
                        handleClose()
                    }}
                >
                    Zapisz zdarzenie
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

ModalProcesSprawozdania.propTypes = {
    currPozycja: PropTypes.object,
    show: PropTypes.bool,
    handleClose: PropTypes.func,
    proces: PropTypes.array
}